import { Component, OnInit, signal } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AppFooterComponent } from './shared/components/footer/app-footer.component';
import { SiteFooterComponent } from './shared/components/footer/footer.component';
import { SiteHeaderComponent } from './shared/components/header/header.component';
import { NotificationComponent } from './shared/components/notification/notification.component';
import { CookieConsentComponent } from './shared/components/cookie-consent/cookie-consent.component';
import { CommonModule } from '@angular/common';
import { FixedBottomBarComponent } from './pages/public/landing/fixed-bottom-bar/fixed-bottom-bar.component';
import { RefundModalComponent } from './pages/public/landing/refund-modal/refund-modal.component';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { environment } from '../environments/environment';
import { CookieService } from './core/services/cookie.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    SiteHeaderComponent,
    SiteFooterComponent,
    AppFooterComponent,
    NotificationComponent,
    CookieConsentComponent,
    FixedBottomBarComponent,
    RefundModalComponent,
    CommonModule
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  protected readonly title = signal('abig');
  isDashboardRoute = false;
  isPricingRoute = false;
  isFaqRoute = false;
  isContactRoute = false;
  isQrRoute = false;
  isCookieConsentVisible = true;
  isBottomBarVisible = false; // show only when presale mode is active
  isRefundModalOpen = false;
  hasBasketItems = false;

  constructor(
    private router: Router,
    private http: HttpClient,
    private cookieService: CookieService
  ) {
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        this.isDashboardRoute = url.startsWith('/dashboard') || 
                               url.startsWith('/login') || 
                               url.startsWith('/forgot-password') || 
                               url.startsWith('/otp-verification') ||
                               url.startsWith('/reset-password') ||
                               url.startsWith('/active-code');
        this.isPricingRoute = url.startsWith('/pricing');
        this.isFaqRoute     = url.startsWith('/faq');
        this.isContactRoute = url.startsWith('/contact');
        this.isQrRoute      = url.startsWith('/qr');
        
        // Update bottom bar visibility based on current route
        this.updateBottomBarVisibility();
      }
    });

    // Check if user has already accepted cookies
    // First check cookies, then fallback to localStorage for backward compatibility
    let cookieConsent = this.cookieService.getCookie('cookieConsent');
    if (!cookieConsent && typeof localStorage !== 'undefined') {
      cookieConsent = localStorage.getItem('cookieConsent');
      // If found in localStorage, migrate to cookie
      if (cookieConsent) {
        this.cookieService.setCookie('cookieConsent', cookieConsent);
      }
    }
    
    if (cookieConsent === 'accepted' || cookieConsent === 'managed') {
      this.isCookieConsentVisible = false;
      document.body.classList.remove('cookie-consent-visible');
    } else {
      this.isCookieConsentVisible = true;
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        document.body.classList.add('cookie-consent-visible');
      }, 0);
    }
  }

  ngOnInit(): void {
    this.loadPresaleStatus();
  }

  onCookieConsentClose(): void {
    this.isCookieConsentVisible = false;
    document.body.classList.remove('cookie-consent-visible');
  }

  onCookieConsentAccept(): void {
    // Set all cookies to accepted with 60 days expiration
    this.cookieService.setCookie('cookieConsent', 'accepted');
    this.cookieService.setCookie('marketingCookies', 'true');
    this.cookieService.setCookie('functionalCookies', 'true');
    this.cookieService.setCookie('analyticsCookies', 'true');
    this.cookieService.setCookie('performanceCookies', 'true');
    this.cookieService.setCookie('socialMediaCookies', 'true');
    this.isCookieConsentVisible = false;
    document.body.classList.remove('cookie-consent-visible');
  }

  onCookieConsentManage(): void {
    // Cookie preferences are managed in the component itself
    // This method is kept for compatibility but the actual saving happens in the component
    this.isCookieConsentVisible = false;
    document.body.classList.remove('cookie-consent-visible');
  }

  onBottomBarMoreInfo(): void {
    this.hasBasketItems = this.checkBasketHasItems();
    this.isRefundModalOpen = true;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  }

  private checkBasketHasItems(): boolean {
    if (typeof localStorage === 'undefined') return false;
    try {
      const savedState = localStorage.getItem('pricingWizardState');
      if (!savedState) return false;
      const state = JSON.parse(savedState);
      return !!(state?.selectedPackage || state?.staticSelectedPlan);
    } catch {
      return false;
    }
  }

  onBottomBarGetStarted(): void {
    this.router.navigate(['/pricing']);
  }

  onBuyNowClick(): void {
    this.router.navigate(['/pricing']);
    this.closeRefundModal();
  }

  closeRefundModal(): void {
    this.isRefundModalOpen = false;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  onBottomBarClose(): void {
    this.isBottomBarVisible = false;
    // Store in sessionStorage that user closed the bar for this tab
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('fixedBottomBarClosed', 'true');
    }
  }

  private loadPresaleStatus(): void {
    this.http.get<any>(`${environment.apiUrl}/presale-mode/public/status`).subscribe({
      next: (res) => {
        const presaleActive = !!res?.data?.isActive;
        // Store presale status and update visibility based on current route
        this.presaleStatusActive = presaleActive;
        this.updateBottomBarVisibility();
      },
      error: (err) => {
        this.presaleStatusActive = false;
        this.updateBottomBarVisibility();
      },
    });
  }

  private presaleStatusActive = false;

  private updateBottomBarVisibility(): void {
    const currentUrl = this.router.url;
    const shouldHideOnRoute = currentUrl.startsWith('/login') || 
                              currentUrl.startsWith('/forgot-password') || 
                              currentUrl.startsWith('/otp-verification') ||
                              currentUrl.startsWith('/pricing') ||
                              currentUrl.startsWith('/qr');
    
    // Check if user has closed the bar in this tab session
    const isBarClosedInSession = typeof sessionStorage !== 'undefined' && 
                                  sessionStorage.getItem('fixedBottomBarClosed') === 'true';
    
    // Show bottom bar only if presale is active AND not on excluded routes AND not closed in this session
    this.isBottomBarVisible = this.presaleStatusActive && !shouldHideOnRoute && !isBarClosedInSession;
  }
}
